import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type ClientSummaryDto } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ClientEditForm, emptyClientForm, type ClientFormData } from './ClientDetailPage'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X, Plus, AlertCircle, PowerOff } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import { useAdminMoney } from '@/hooks/useMoney'
import { useAdminDates } from '@/hooks/useDates'

type SortKey = 'name' | 'location' | 'staffCount' | 'bookingCount' | 'totalInvoiced' | 'outstandingAmount' | 'lastBookingDate' | 'createdAt'
type SortDir = 'asc' | 'desc'

function SortIcon({ col, sortKey, dir }: { col: SortKey; sortKey: SortKey; dir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={13} className="text-gray-300 ml-1 inline" />
  return dir === 'asc'
    ? <ChevronUp size={13} className="text-indigo-500 ml-1 inline" />
    : <ChevronDown size={13} className="text-indigo-500 ml-1 inline" />
}

function Th({ label, col, sortKey, dir, onSort, className }: {
  label: string; col: SortKey; sortKey: SortKey; dir: SortDir
  onSort: (col: SortKey) => void
  className?: string
}) {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap ${className ?? ''}`}
      onClick={() => onSort(col)}
    >
      {label}<SortIcon col={col} sortKey={sortKey} dir={dir} />
    </th>
  )
}

function location(c: ClientSummaryDto) {
  return [c.city, c.county].filter(Boolean).join(', ') || null
}

export default function ClientsListPage() {
  const { formatDate } = useAdminDates()
  const fmtDate = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const { fmt: fmtMoney } = useAdminMoney()
  useTopBar(<span className="text-sm font-medium text-gray-900">Clients</span>)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<'active' | 'deactivated'>('active')
  const [showForm, setShowForm] = useState(false)
  const [formError, setFormError] = useState('')

  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['client-summaries'],
    queryFn: () => adminApi.getClientSummaries().then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: ClientFormData) => adminApi.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-summaries'] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setShowForm(false)
      setFormError('')
    },
    onError: () => setFormError('Failed to create client.'),
  })

  const handleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(col); setSortDir(col === 'name' || col === 'location' ? 'asc' : 'desc') }
  }

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => adminApi.setClientActive(id, true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['client-summaries'] }),
  })

  const tabClients = useMemo(() =>
    clients.filter(c => tab === 'active' ? c.isActive : !c.isActive),
    [clients, tab])

  const filteredBase = useMemo(() => {
    const q = search.toLowerCase().trim()
    return tabClients
      .filter(c => !q || [c.name, c.contactName ?? '', c.contactEmail, c.city ?? '', c.county ?? '']
        .some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        let cmp = 0
        switch (sortKey) {
          case 'name':              cmp = a.name.localeCompare(b.name); break
          case 'location':          cmp = (location(a) ?? '').localeCompare(location(b) ?? ''); break
          case 'staffCount':        cmp = a.staffCount - b.staffCount; break
          case 'bookingCount':      cmp = a.bookingCount - b.bookingCount; break
          case 'totalInvoiced':     cmp = a.totalInvoiced - b.totalInvoiced; break
          case 'outstandingAmount': cmp = a.outstandingAmount - b.outstandingAmount; break
          case 'lastBookingDate':   cmp = (a.lastBookingDate ?? '').localeCompare(b.lastBookingDate ?? ''); break
          case 'createdAt':         cmp = a.createdAt.localeCompare(b.createdAt); break
        }
        return sortDir === 'asc' ? cmp : -cmp
      })
  }, [tabClients, search, sortKey, sortDir])

  const filtered = filteredBase
  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(filtered)

  const thProps = { sortKey, dir: sortDir, onSort: handleSort }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-end mb-6">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{filtered.length} of {tabClients.length}</span>
          <Button onClick={() => setShowForm(v => !v)} size="sm" className="gap-2">
            <Plus size={16} /> Add client
          </Button>
        </div>
      </div>

      {/* Add client form */}
      {showForm && (
        <Card className="mb-6">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base">New client</CardTitle>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setShowForm(false)}>
              <X size={15} />
            </Button>
          </CardHeader>
          <CardContent>
            <ClientEditForm
              initial={emptyClientForm}
              onSubmit={data => createMutation.mutate(data)}
              onCancel={() => setShowForm(false)}
              pending={createMutation.isPending}
              submitLabel="Save client"
            />
            {formError && <p className="mt-3 text-sm text-red-600">{formError}</p>}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {([
          { id: 'active',      label: 'Active',      count: clients.filter(c => c.isActive).length },
          { id: 'deactivated', label: 'Deactivated', count: clients.filter(c => !c.isActive).length },
        ] as const).map(t => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setSearch('') }}
            className={`px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer border-b-2 -mb-px flex items-center gap-2 ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`text-xs rounded-full px-1.5 py-0.5 ${tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, location…"
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-gray-400 py-8">
            {tab === 'deactivated' ? 'No deactivated clients.' : clients.length === 0 ? 'No clients yet.' : 'No clients match your search.'}
          </p>
        )}
        {paged.map(c => (
          <div
            key={c.id}
            onClick={() => navigate(`/admin/clients/${c.id}`)}
            className={`bg-white border border-gray-200 rounded-lg p-4 cursor-pointer ${c.isActive ? 'hover:border-indigo-200' : 'bg-gray-50 opacity-75'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">{c.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {c.contactName ? `${c.contactName} · ` : ''}{c.contactEmail}
                </p>
              </div>
              {c.outstandingAmount > 0 && (
                <span className="flex items-center gap-1 text-red-600 text-xs font-medium shrink-0">
                  <AlertCircle size={12} />
                  {fmtMoney(c.outstandingAmount)}
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              {location(c) && <span>{location(c)}</span>}
              <span>{c.staffCount} staff</span>
              <span>{c.bookingCount} bookings</span>
              {c.lastBookingDate && <span>Last: {fmtDate(c.lastBookingDate)}</span>}
            </div>
            {tab === 'deactivated' && (
              <div className="mt-3" onClick={e => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs text-green-700 border-green-200 hover:bg-green-50"
                  onClick={() => reactivateMutation.mutate(c.id)}
                  disabled={reactivateMutation.isPending}
                >
                  <PowerOff size={12} /> Reactivate
                </Button>
              </div>
            )}
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th label="Organisation" col="name"              {...thProps} />
                <Th label="Location"     col="location"          {...thProps} />
                <Th label="Staff"        col="staffCount"        {...thProps} className="text-right" />
                <Th label="Bookings"     col="bookingCount"      {...thProps} className="text-right" />
                <Th label="Invoiced"     col="totalInvoiced"     {...thProps} className="text-right" />
                <Th label="Outstanding"  col="outstandingAmount" {...thProps} className="text-right" />
                <Th label="Last booking" col="lastBookingDate"   {...thProps} />
                <Th label="Member since" col="createdAt"         {...thProps} />
                {tab === 'deactivated' && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-12">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={tab === 'deactivated' ? 9 : 8} className="text-center text-gray-400 py-12">
                  {tab === 'deactivated' ? 'No deactivated clients.' : clients.length === 0 ? 'No clients yet.' : 'No clients match your search.'}
                </td></tr>
              )}
              {paged.map(c => (
                <tr
                  key={c.id}
                  className={`transition-colors cursor-pointer ${c.isActive ? 'hover:bg-indigo-50/40' : 'bg-gray-50/60 hover:bg-gray-100/60 opacity-75'}`}
                  onClick={() => navigate(`/admin/clients/${c.id}`)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate max-w-48">
                      {c.contactName ? `${c.contactName} · ` : ''}{c.contactEmail}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {location(c) ?? <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">{c.staffCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">{c.bookingCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-900 whitespace-nowrap">
                    {fmtMoney(c.totalInvoiced)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                    {c.outstandingAmount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                        <AlertCircle size={12} />
                        {fmtMoney(c.outstandingAmount)}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {c.lastBookingDate ? fmtDate(c.lastBookingDate) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs">{fmtDate(c.createdAt)}</td>
                  {tab === 'deactivated' && (
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1.5 text-xs text-green-700 border-green-200 hover:bg-green-50"
                        onClick={() => reactivateMutation.mutate(c.id)}
                        disabled={reactivateMutation.isPending}
                      >
                        <PowerOff size={12} /> Reactivate
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>
    </div>
  )
}
